import { useEffect, useState } from "react";
import { posthog } from "@/config/posthog.config";

export interface FeatureFlagState {
  /** Whether the flag is enabled. False until resolved, so gated UI never flashes. */
  enabled: boolean;
  /** True once PostHog has loaded flags (or immediately in DEV). */
  loaded: boolean;
}

/**
 * Reactively reads a PostHog feature flag via `onFeatureFlags`.
 *
 * PostHog is NOT initialized on localhost (see `posthog.config.ts`), so flags
 * never resolve in local dev. To keep gated features workable locally, this
 * defaults to **enabled in DEV builds**. Deployed environments evaluate the
 * real flag and stay `enabled: false` until it resolves — so callers can keep
 * gated UI hidden until `loaded` is true and avoid a flash.
 */
export function useFeatureFlag(flagKey: string): FeatureFlagState {
  const [state, setState] = useState<FeatureFlagState>(() => {
    if (import.meta.env.DEV) return { enabled: true, loaded: true };
    return { enabled: posthog.isFeatureEnabled(flagKey) === true, loaded: false };
  });

  useEffect(() => {
    if (import.meta.env.DEV) return;
    // Re-sync immediately for the current flagKey: the lazy useState initializer
    // only ran for the very first flagKey, so a changed flagKey must re-evaluate
    // here rather than keep the stale flag's state.
    setState({ enabled: posthog.isFeatureEnabled(flagKey) === true, loaded: false });
    // Fires once flags are loaded and again on any change; may return undefined.
    const unsubscribe = posthog.onFeatureFlags(() => {
      setState({ enabled: posthog.isFeatureEnabled(flagKey) === true, loaded: true });
    });
    return () => unsubscribe?.();
  }, [flagKey]);

  return state;
}
